/**
 * InlineApprovalRow — approval action rendered inside the chat
 * (Timeline) tab when a section needs writer sign-off.
 *
 * Spec 29 Phase 2. Chat-first complement to ApprovalPopout (which
 * still renders inside LoomSpine for the Spine tab). Both surfaces
 * read the SAME ``state.pendingApproval`` and wire to the SAME
 * accept / open-review handlers — clicking either dismisses the
 * approval globally. Writers see the request whichever tab they're
 * looking at; no missed approvals.
 *
 * SRP: pure presentation. Takes the PendingApproval + two handlers;
 * emits intent. Doesn't know about the reducer.
 *
 * # DECISION: flat chat-row aesthetic (not the card aesthetic of
 * #   ApprovalPopout).
 * # WHY: the Timeline renders flat rows per event. A card popping
 * #      above them would break the chat cadence writers are
 * #      scanning. The tier chip + inline buttons give the same
 * #      information density with a grammar that fits the feed.
 * # DATE: 2026-04-23
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import type { PendingApproval } from "../state";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";

// ---------------------------------------------------------------------------
// Tier vocabulary — mirrors ApprovalPopout's TIER_STYLES for
// consistency across surfaces. Label copy matches so writers see
// the same severity on either tab.
// ---------------------------------------------------------------------------

interface TierPresentation {
  accent: string;
  badgeColor: string;
  badgeBackground: string;
  label: string;
}

const TIER_VOCABULARY: Record<PendingApproval["tier"], TierPresentation> = {
  info: {
    accent: brandColors.mid,
    badgeColor: "#fff",
    badgeBackground: brandColors.primary,
    label: "FYI",
  },
  block: {
    accent: statusColors.warn,
    badgeColor: "#7C2D12",
    badgeBackground: "#FED7AA",
    label: "NEEDS REVIEW",
  },
  "high-stakes": {
    accent: statusColors.danger,
    badgeColor: "#fff",
    badgeBackground: statusColors.danger,
    label: "BLOCKER",
  },
};

/** Pure lookup — exported for tests so copy drift is pinned. */
export function tierPresentation(tier: PendingApproval["tier"]): TierPresentation {
  return TIER_VOCABULARY[tier];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface InlineApprovalRowProps {
  approval: PendingApproval;
  /** Writer accepted the call. Shell dispatches APPROVAL_RESOLVED. */
  onAccept: () => void;
  /** Writer wants to inspect findings — focuses the section +
   *  opens Inspector's Review tab. APPROVAL_RESOLVED fires too
   *  (same contract as the spine's ApprovalPopout). */
  onOpenReview: () => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    // Accent border on the left mirrors the chat-app convention for
    // "you need to act here". Tier colour applied inline.
    padding: "10px 12px 10px 14px",
    borderLeft: "3px solid currentColor", // overridden via inline style per tier
    background: surfaceColors.paperDim,
    borderTop: `1px solid ${surfaceColors.line}`,
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.body,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "10px",
    fontFamily: fonts.mono,
  },
  badge: {
    fontWeight: 700,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    padding: "1px 6px",
    borderRadius: "2px",
    fontSize: "9.5px",
  },
  sectionLabel: {
    color: inkColors.ink3,
    fontWeight: 600,
  },
  question: {
    fontSize: "12px",
    color: inkColors.ink,
    lineHeight: 1.45,
    fontWeight: 500,
  },
  context: {
    fontSize: "11px",
    color: inkColors.ink3,
    lineHeight: 1.4,
    fontStyle: "italic",
    borderLeft: `2px solid ${surfaceColors.lineStrong}`,
    padding: "2px 8px",
  },
  actions: {
    display: "flex",
    gap: "8px",
    marginTop: "2px",
  },
  buttonPrimary: {
    background: brandColors.primary,
    color: "#fff",
    border: "none",
    padding: "5px 12px",
    borderRadius: "3px",
    fontSize: "11.5px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: fonts.body,
    "&:hover": {
      filter: "brightness(1.05)",
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  buttonGhost: {
    background: "transparent",
    color: inkColors.ink3,
    border: `1px solid ${surfaceColors.lineStrong}`,
    padding: "5px 12px",
    borderRadius: "3px",
    fontSize: "11.5px",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: fonts.body,
    "&:hover": {
      color: inkColors.ink,
      // Shorthand override per Griffel's type rules.
      border: `1px solid ${inkColors.ink3}`,
    },
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InlineApprovalRow: React.FC<InlineApprovalRowProps> = ({ approval, onAccept, onOpenReview }) => {
  const styles = useStyles();
  const tier = tierPresentation(approval.tier);

  return (
    <section
      className={styles.root}
      aria-label={`Approval needed for section ${approval.sectionNumber}`}
      style={{
        borderLeftColor: tier.accent,
        color: tier.accent, // feeds the currentColor on the border
      }}
    >
      <div className={styles.header}>
        <span
          className={styles.badge}
          style={{
            background: tier.badgeBackground,
            color: tier.badgeColor,
          }}
        >
          {tier.label}
        </span>
        <span className={styles.sectionLabel}>§{approval.sectionNumber}</span>
      </div>
      <div className={styles.question}>{approval.question}</div>
      {approval.contextExcerpt && <div className={styles.context}>{approval.contextExcerpt}</div>}
      <div className={styles.actions}>
        <button type="button" className={styles.buttonPrimary} onClick={onAccept}>
          Mark resolved
        </button>
        <button type="button" className={styles.buttonGhost} onClick={onOpenReview}>
          Open review
        </button>
      </div>
    </section>
  );
};

export default InlineApprovalRow;
